import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast, cn, TheBackyardLogo } from '@kss/ui';
import { useConfig } from '@kss/theme';
import { authService, type ApiAuthUser } from '@kss/api';
import { handoffStaffSession } from '../lib/panel';

const GRADS = [
  'linear-gradient(135deg,#FFD9C7,#F2541B)',
  'linear-gradient(135deg,#CFE3FF,#2D7FF9)',
  'linear-gradient(135deg,#D5EFE0,#1B9C5D)',
  'linear-gradient(135deg,#FBE7C6,#F4A920)'
];

const EMPTY_PIN = ['', '', '', '', '', ''];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase()) || 'U';
}

/**
 * Staff PIN login (POS / "workplace") — pick a profile, punch a 6-digit PIN entered
 * in the same 6-box style as the customer OTP. Wired to `GET /api/auth/staff` +
 * `POST /api/auth/pin-login`; the dev PIN is 102030 (same as the customer OTP code).
 */
export default function StaffLogin() {
  const { logo, brand } = useConfig();
  const [profiles, setProfiles] = useState<ApiAuthUser[]>([]);
  const [selected, setSelected] = useState(0);
  const [pin, setPin] = useState<string[]>(EMPTY_PIN);
  const [busy, setBusy] = useState(false);
  const pinRefs = useRef<(HTMLInputElement | null)[]>([]);

  const toast = useToast();

  useEffect(() => {
    authService.staff().then(setProfiles).catch(() => toast.show('Could not load staff', 'error'));
  }, [toast]);

  const staff = profiles[selected];
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long' });

  function selectStaff(i: number) {
    setSelected(i);
    setPin(EMPTY_PIN);
    setTimeout(() => pinRefs.current[0]?.focus(), 50);
  }

  function onPinChange(i: number, val: string) {
    const d = val.replace(/\D/g, '').slice(-1);
    setPin((prev) => {
      const next = [...prev];
      next[i] = d;
      return next;
    });
    if (d && i < 5) pinRefs.current[i + 1]?.focus();
  }

  function onPinKey(i: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !pin[i] && i > 0) pinRefs.current[i - 1]?.focus();
    if (e.key === 'Enter') signIn();
  }

  async function signIn() {
    if (!staff) return;
    const code = pin.join('');
    if (code.length < 6) {
      toast.show('Enter your 6-digit PIN', 'error');
      return;
    }
    setBusy(true);
    try {
      const res = await authService.pinLogin(staff.username, code);
      // Staff/admin belong in the admin+POS panel, not the customer storefront —
      // hand the session to the panel app and full-navigate there.
      const url = handoffStaffSession({
        user: { id: 0, name: res.user.fullName, initials: initialsOf(res.user.fullName) },
        token: res.token,
        refreshToken: res.refreshToken,
        sessionExpiresAt: res.sessionExpiresAt,
        role: res.user.role,
        permissions: res.permissions
      });
      toast.show(`Signed in as ${res.user.fullName} · ${res.user.role}`, 'success');
      window.location.assign(url);
    } catch {
      toast.show('Wrong PIN', 'error');
      setPin(EMPTY_PIN);
      setBusy(false);
      pinRefs.current[0]?.focus();
    }
  }

  return (
    <div className='flex min-h-screen items-center justify-center bg-surface-content p-4 font-sans text-content-primary'>
      <div className='flex w-full max-w-[620px] flex-col overflow-hidden rounded-3xl bg-surface-card shadow-pop sm:flex-row'>
        {/* Brand panel */}
        <div className='flex flex-col gap-4 bg-[image:var(--gradient-primary)] p-6 text-white sm:w-[220px]'>
          <TheBackyardLogo size={34} src={logo || undefined} className='text-white' />
          <div className='sm:mt-auto'>
            <div className='font-display text-[22px] font-extrabold leading-tight'>Staff Login</div>
            <div className='mt-1.5 text-[12.5px] opacity-90'>{brand}</div>
            <div className='mt-3 text-[12px] opacity-80'>{today} · 9:41 AM</div>
          </div>
        </div>

        {/* PIN panel */}
        <div className='flex flex-1 flex-col p-6'>
          <div className='mb-3 text-[11px] font-semibold uppercase tracking-wider text-content-muted'>
            Select your profile
          </div>
          {profiles.length === 0 ? (
            <div className='mb-5 text-[13px] text-content-muted'>Loading staff…</div>
          ) : (
            <div className='mb-6 flex flex-wrap gap-3'>
              {profiles.map((s, i) => (
                <button key={s.username} onClick={() => selectStaff(i)} className='text-center'>
                  <div
                    className={cn(
                      'mx-auto flex h-[46px] w-[46px] items-center justify-center rounded-xl font-display text-[15px] font-bold text-white',
                      i === selected && 'ring-2 ring-primary ring-offset-2 ring-offset-surface-card'
                    )}
                    style={{ background: GRADS[i % GRADS.length] }}
                  >
                    {initialsOf(s.fullName)}
                  </div>
                  <div
                    className={cn(
                      'mt-2 text-[10.5px] font-semibold',
                      i === selected ? 'text-primary' : 'text-content-secondary'
                    )}
                  >
                    {s.fullName}
                  </div>
                  <div className='text-[9.5px] text-content-muted'>{s.role}</div>
                </button>
              ))}
            </div>
          )}

          <div className='text-[13px] text-content-secondary'>
            Enter 6-digit PIN for <b className='font-display text-content-primary'>{staff?.fullName ?? '—'}</b>
          </div>

          {/* 6-box PIN entry (same as the customer OTP). */}
          <div className='mt-3 grid grid-cols-6 gap-2'>
            {pin.map((d, i) => (
              <input
                key={i}
                ref={(el) => {
                  pinRefs.current[i] = el;
                }}
                value={d}
                onChange={(e) => onPinChange(i, e.target.value)}
                onKeyDown={(e) => onPinKey(i, e)}
                inputMode='numeric'
                type='password'
                maxLength={1}
                aria-label={`PIN digit ${i + 1}`}
                className={cn(
                  'h-14 w-full rounded-xl border-[1.5px] text-center font-display text-xl font-bold text-content-primary focus:outline-none',
                  d ? 'border-primary' : 'border-border focus:border-primary'
                )}
              />
            ))}
          </div>

          <button
            onClick={() => toast.show('PIN reset — ask your manager', 'info')}
            className='mt-3 self-start text-[11.5px] font-semibold text-content-muted hover:text-primary'
          >
            Forgot PIN?
          </button>

          <button
            onClick={signIn}
            // Gated on a complete PIN and a chosen profile, rather than only reporting the
            // problem after the click.
            disabled={busy || !staff || pin.join('').length < 6}
            className='mt-5 rounded-xl bg-primary py-3.5 font-display text-[15px] font-bold text-primary-foreground shadow-cta disabled:opacity-60'
          >
            {busy ? 'Signing in…' : 'Login'}
          </button>
          <Link
            to='/login'
            className='mt-3 text-center text-[12px] font-semibold text-content-secondary hover:text-primary'
          >
            ← Customer login
          </Link>
        </div>
      </div>
    </div>
  );
}
